import { nameFields } from './individualName';

export const normalizePersonName = value => String(value || '').normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase().replace(/\s+/g, ' ');
export const personRef = (collection, row, index) => `${collection}:${row?._rowId || `index-${index}`}`;
const collections = ['shareholders', 'ubos', 'controllingPersonsNatural'];
const individual = row => !['entity', 'company', 'trust', 'foundation'].includes(String(row?.holderType || '').toLowerCase());
export function ownershipPeople(ownership = {}) {
  return collections.flatMap(collection => (ownership[collection] || []).flatMap((row, index) =>
    individual(row) && row?.name?.trim() ? [{ row, collection, index, ref: personRef(collection, row, index), role: collection === 'shareholders' ? 'Direct shareholder' : 'Controlling person' }] : []));
}
export function linkedPerson(ownership, signatory) {
  const link = signatory?._personLink;
  return link?.rowId ? ownershipPeople(ownership).find(person => person.collection === link.collection && person.row._rowId === link.rowId) : undefined;
}
export function personIdentity(row = {}) {
  return { name: String(row.name || ''), ...nameFields(row), nationality: row.nationality || '',
    countryOfResidence: row.countryOfResidence || row.domicile || '',
    dateOfBirth: row.dateOfBirth || row.dob || row.dobOrIncorporationDate || '' };
}
export function resolvedSignatory(ownership, row) {
  const person = linkedPerson(ownership, row);
  return person ? { ...row, ...personIdentity(person.row) } : row;
}
export const identityFields = { dateOfBirth: 'Date of birth', nationality: 'Nationality', countryOfResidence: 'Domicile', givenNames: 'Given names', familyName: 'Family name' };
export function identityConflicts(signatory, source) {
  const a = personIdentity(signatory), b = personIdentity(source);
  return Object.keys(identityFields).filter(key => a[key] && b[key] && normalizePersonName(a[key]) !== normalizePersonName(b[key]));
}
export function personSuggestionKey(person) {
  return `${person.ref}|${JSON.stringify(personIdentity(person.row))}`;
}
export function sameNamePeople(ownership, signatory, { includeDifferent = false } = {}) {
  const name = normalizePersonName(signatory?.name);
  if (!name || linkedPerson(ownership, signatory)) return [];
  return ownershipPeople(ownership).filter(person => normalizePersonName(person.row.name) === name &&
    (includeDifferent || !(signatory._differentPeople || []).includes(personSuggestionKey(person))));
}
export function linkSignatory(ownership, index, person, choices = {}) {
  // Re-read the source: never link a deleted/replaced row from a stale prompt.
  const source = ownershipPeople(ownership).find(item => item.ref === person.ref);
  const signatory = ownership.signatories[index];
  if (!source || !signatory) return ownership;
  const conflicts = identityConflicts(signatory, source.row);
  if (conflicts.some(key => !['source', 'signatory'].includes(choices[key]))) return ownership;
  const sourceIdentity = personIdentity(source.row), signatoryIdentity = personIdentity(signatory);
  const identity = Object.fromEntries(Object.keys(sourceIdentity).map(key => [key,
    choices[key] === 'signatory' ? signatoryIdentity[key] : sourceIdentity[key] || signatoryIdentity[key]]));
  const rowId = source.row._rowId || `person_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  const next = { ...ownership, [source.collection]: ownership[source.collection].map((row, i) =>
    i === source.index ? { ...row, ...identity, _rowId: rowId } : row) };
  next.signatories = ownership.signatories.map((row, i) => i === index
    ? { ...row, ...identity, _personLink: { collection: source.collection, rowId }, _differentPeople: [] }
    : row);
  return syncLinkedSignatories(next);
}
export function syncLinkedSignatories(ownership) {
  if (!Array.isArray(ownership?.signatories)) return ownership;
  return { ...ownership, signatories: ownership.signatories.map(row => resolvedSignatory(ownership, row)) };
}
// Linked signatory and shareholder / controlling person are one person: an identity change made on either row
// reaches the other one. Other keys of the patch (capacity, %, …) stay on the edited row only.
const linkedIdentityKeys = ['name', 'givenNames', 'familyName', 'nationality', 'countryOfResidence', 'dateOfBirth'];
const identityAliases = { countryOfResidence: 'domicile', dateOfBirth: 'dob' };
function withIdentity(row, identity) {
  const next = { ...row, ...identity };
  Object.entries(identityAliases).forEach(([key, alias]) => {
    if (key in identity && alias in row) next[alias] = identity[key];
  });
  return next;
}
export function editLinkedPerson(ownership, collection, index, patch = {}) {
  const rows = Array.isArray(ownership?.[collection]) ? ownership[collection] : [];
  if (!rows[index]) return ownership;
  const identity = Object.fromEntries(Object.entries(patch).filter(([key]) => linkedIdentityKeys.includes(key)));
  const edited = { ...rows[index], ...patch };
  const next = { ...ownership, [collection]: rows.map((row, i) => (i === index ? edited : row)) };
  if (!Object.keys(identity).length) return next;
  if (collection === 'signatories') {
    const source = linkedPerson(next, edited);
    if (!source) return next;
    next[source.collection] = next[source.collection].map((row, i) => (i === source.index ? withIdentity(row, identity) : row));
    return next;
  }
  if (!edited._rowId || !Array.isArray(next.signatories)) return next;
  next.signatories = next.signatories.map(row => (row?._personLink?.collection === collection && row._personLink.rowId === edited._rowId
    ? withIdentity(row, identity) : row));
  return next;
}
